import React, { useState } from "react";
import { StyleSheet, Pressable, Text, View } from "react-native";
import { TTabName } from "../../../utils/types";
import { Star } from "../../../assets";
import { RainbowBorder } from ".";

type TabProps = {
  tabNames: TTabName[];
  tabContent: React.ReactNode[];
  tabRight?: React.ReactNode;
  currentTab?: TTabName;
  onTabPress?: (tabKey: TTabName) => void;
  initialTab?: string;
  disabledTabIndexes?: number[];
  highlightedTabIndex?: number; // ✅ new prop
};

const Tab: React.FC<TabProps> = ({
  tabNames,
  tabRight = null,
  tabContent,
  onTabPress = null,
  initialTab = tabNames[0],
  disabledTabIndexes = [],
  currentTab = "",
  highlightedTabIndex, // ✅ destructure it
}) => {
  const [selectedTab, setSelectedTab] = useState<string>(initialTab);

  React.useEffect(() => {
    if (currentTab) setSelectedTab(currentTab);
  }, [currentTab]);

  const activeTab = currentTab || selectedTab;

  return (
    <>
      <View style={[styles.tabContainer]}>
        {tabNames.map((tabName, index) => {
          const isActive = tabName === activeTab;
          const textColor = isActive ? "#000000" : "#868B97";
          const displayLabel =
            tabName === "Profile"
              ? "1 Profile"
              : tabName === "Appearance"
                ? "2 Appearance"
                : tabName === "Deploy"
                  ? "3 Deploy"
                  : tabName === "Form Format"
                    ? "3 Pick Form Format"
                    : tabName;

          if (disabledTabIndexes.includes(index)) {
            return (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text
                  style={[
                    styles.tabText,
                    styles.disabledText,
                    { color: textColor },
                  ]}
                >
                  {displayLabel}
                </Text>
                {tabName === "Deploy" && <Star color={textColor} />}
              </View>
            );
          }

          const pressableTab = (
            <Pressable
              key={index}
              style={[isActive ? styles.selectedTab : styles.unselectedTab]}
              onPress={() => {
                setSelectedTab(tabName);
                if (onTabPress) onTabPress(tabName);
              }}
            >
              <Text style={[styles.tabText, { color: textColor }]}>
                {displayLabel}
              </Text>
              {tabName === "Deploy" && <Star color={textColor} />}
            </Pressable>
          );

          return index === highlightedTabIndex ? (
            <RainbowBorder key={index} isActive>
              {pressableTab}
            </RainbowBorder>
          ) : (
            <View key={index}>{pressableTab}</View>
          );
        })}
        <View style={{ marginLeft: "auto" }}>{tabRight}</View>
        <View style={styles.grayLine} />
      </View>
      {tabContent.map((content, index) => (
        <View
          key={index}
          style={{
            flex: 1,
            display: tabNames[index] === activeTab ? "flex" : "none",
          }}
        >
          {content}
        </View>
      ))}
    </>
  );
};

const styles = StyleSheet.create({
  tabContainer: {
    flexDirection: "row",
    width: "100%",
    position: "relative",
    gap: 24,
    marginBottom: 12,
  },
  grayLine: {
    bottom: 0,
    left: 0,
    width: "100%",
    borderBottomWidth: 1,
    flexDirection: "row",
    borderBottomColor: "#DDDEE0",
    position: "absolute",
  },
  tabText: {
    fontSize: 20,
    fontFamily: "Urbanist600",
  },
  unselectedTab: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomColor: "transparent",
  },
  selectedTab: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomColor: "#000000",
    borderBottomWidth: 1,
    zIndex: 2,
  },
  disabledText: {
    paddingVertical: 12,
    borderBottomColor: "transparent",
    // @ts-ignore
    cursor: "not-allowed",
  },
});

export default Tab;
